import { Link } from 'react-router-dom';
import { useLanguage } from '@/components/utils/useLanguage';
import { Button } from '@/components/ui/button';
import FaqItem from '@/components/layout/FaqItem';
import { container, sectionPadding, sectionBgWhite } from '@/lib/ds';

export default function AboutFaqSection() {
  const { t } = useLanguage();

  return (
    <section className={`${sectionPadding} ${sectionBgWhite}`}>
      <div className={container}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: About text */}
          <div>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-3">
              {t('Über Basel mit Kindern', 'About Basel with kids')}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              {t('Familienausflüge in Basel und Region – Entdeckt, getestet und ehrlich bewertet.', 'Family trips in Basel and the surrounding area – discovered, tried and tested, and honestly reviewed.')}
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                {t(
                  'Ob Indoorspielplatz bei Regen, Tierpark zum Nulltarif oder Museum für Neugierige: auf «Basel mit Kindern» findest du die besten Kinderaktivitäten und Familienausflüge in Basel und der Region, die wirklich funktionieren. ',
                  "Whether it’s an indoor play centre when it’s raining, a free zoo or a museum for curious minds: on ‘Basel mit Kindern’ you’ll find the best children’s activities and family days out in Basel and the surrounding areas that really work."
                )}
              </p>
              <p>
                {t(
                  'Jede Ausflugsidee kannst du nach Alter, Wetter, Dauer und Preis filtern, damit aus der Frage «Was machen wir heute?» in wenigen Klicks ein toller Familienausflug wird.',
                  "Each trip idea can be filtered by age, weather, duration and price, so that the question ‘What shall we do today?’ turns into a brilliant family outing in just a few clicks."
                )}
              </p>
              <p>
                {t(
                  'Alle Kinderaktivitäten und Angebote werden persönlich von uns getestet und ehrlich bewertet: wir sagen dir ungeschönt, was begeistert, was nervt und was sich wirklich lohnt.',
                  "All activities for children and offers are personally tested and honestly reviewed: we’ll tell you straight up what’s a bit of a nuisance, what’s brilliant and what’s really worth it."
                )}
              </p>
              <div
                style={{ animationDelay: '0.4s' }}
                className="mt-6 animate-fade-in-up">
                <Link to="/ueber-uns" className="chevron-after flex items-center text-emerald-600 hover:text-emerald-900 hover:underline">
                    {t('Mehr über uns', 'More about us')}
                </Link>
              </div>
            </div>
          </div>

          {/* Right: FAQ Accordion */}
          <div>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-3">
              {t('Häufige Fragen', 'FAQ')}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              {t('Was bietet Basel mit Kindern?', 'What does Basel with Kids offer?')}
            </h2>
            <div className="divide-y divide-gray-200">
              {[
                {
                  q: t('Finde die passende Aktivität mit cleveren Filtern', 'Find the right activity thanks to smart filters'),
                  a: t('Filter dich glücklich: nach Interesse, Alter, Wochentag, Region oder passend zu Tageszeit und Wetter. Auf der Ausflugsideen-Seite findest du so schnell und einfach die passende Aktivitäten und entdeckst z. B. kostenlose Highlights und Geheimtipps in Basel.', "Filter to your heart's content: by interest, age, day of the week, region, or to suit the time of day and weather. This way you can quickly and easily find the right activities and discover, for example, free highlights and hidden gems in Basel.")
                },
                {
                  q: t('Erhalte die wichtigsten Aktivitätsinfos auf einen Blick', 'Find key activity information at a glance'),
                  a: t('Zu jeder Aktivität findest du Öffnungszeiten, Preise, Altersempfehlung, Dauer sowie Tipps zur besten Besuchszeit, Barrierefreiheit, Planung und Anreise kompakt und auf einen Blick – und natürlich eine ehrliche Bewertung. So kannst du in wenigen Minuten entscheiden, ob der Ausflug zu euch passt und einen Versuch wert ist. Alle Infos aktualisieren wir regelmässig.', "For each activity, you'll find opening times, prices, recommended age, duration, as well as tips on the best time to visit, accessibility, planning and how to get there – all in a compact, at-a-glance format – and, of course, an honest review. This means you can decide in just a few minutes whether the outing is right for you and worth a go. We update all this information regularly.")
                },
                {
                  q: t('Speichere deine Favoriten für später', 'Save favourites for later'),
                  a: t('Klicke auf das «Herz-Symbol» bei jeder Aktivität, um sie zu deinen Favoriten hinzuzufügen. Deine gespeicherten Ausflüge findest du jederzeit unter «Favoriten» ganz ohne Anmeldung oder Account.', "Click the heart icon on any activity to add it to your favourites. You can find your saved outings at any time under «Favourites» – no login or account required.")
                },
                {
                  q: t('Plane deine eigene Tour', 'Plan your individual tour'),
                  a: t('Klicke auf das «Tour-Symbol» bei mindestens zwei Aktivitäten, um daraus z. B. eine Tour für das nächste Wochenende zu erstellen. Dazu liefern wir dir die Reisezeit, Gesamtdauer und eine Karten-Übersicht über die Route. Genau wie deine Favoriten kannst du deine Tour mit anderen teilen.', "Click on the «Tour» icon of at least two activities to create for example a tour for your next weekend. We show you the travel time, total duration and an map-overview of the route. Just like your favourites, you can share your tour with others.")
                },
                {
                  q: t('Kostenlos, transparent und unabhängig zum Schutz deiner Daten', 'Free, transparent and independent to protect your data'),
                  a: t('«Basel mit Kindern» ist vollständig kostenlos und unabhängig. Wir finanzieren uns über Partnerschaften mit lokalen Anbietern – was keinen Einfluss auf unsere ehrliche Bewertung hat. Zusätzlich ist uns der Schutz deiner Daten wichtig, die wir nie an Meta/Facebook, Google oder andere preisgeben.', '«Basel mit Kindern» is completely free and independent. We are funded through partnerships with local providers – which in no way influences our honest reviews. Furthermore, we take the protection of your data seriously and will never share it with Meta/Facebook, Google or any other third parties.')
                },
              ].map((item, i) => (
                <FaqItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
            <Link to="/funktionen">
              <Button variant="ghost" className="chevron-after mt-4 flex items-center gap-2 text-emerald-600 hover:text-emerald-700">
                {t('Alle Funktionen im Überblick', 'All features at a glance')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}